"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, Zap, FileText, Briefcase, ShoppingBag, Globe, Bot, Search, TrendingUp, ArrowRight, Sparkles, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const servicesData = [{
  id: "gbp",
  title: "Google Business Profile Optimization",
  category: "Local SEO",
  description: "I personally claim, verify, and fine-tune your Google Business Profile to maximize local map pack visibility and drive direct calls.",
  link: "/LocalSEOPage",
  icon: "MapPin"
}, {
  id: "citations",
  title: "Local Citation Building",
  category: "Local SEO",
  description: "Consistent NAP (Name, Address, Phone) citations across authoritative UK & global directories to build search engine trust.",
  link: "/LocalSEOPage",
  icon: "Building2"
}, {
  id: "reviews",
  title: "Review Management & Reputation",
  category: "Local SEO",
  description: "Proven strategies to cultivate authentic client feedback and manage online reviews as a powerful ranking signal.",
  link: "/LocalSEOPage",
  icon: "Star"
}, {
  id: "schema",
  title: "Local Schema Markup",
  category: "Technical SEO",
  description: "Custom JSON-LD structured data implementation for local businesses, geo-targeting, organizations, and rich search snippets.",
  link: "/TechnicalSeoPage",
  icon: "Code2"
}, {
  id: "technical",
  title: "Technical SEO & Site Speed",
  category: "Technical SEO",
  description: "In-depth crawling, Core Web Vitals optimization, indexability fixes, and clean site architecture audited personally by me.",
  link: "/TechnicalSeoPage",
  icon: "Zap"
}, {
  id: "onpage",
  title: "On-Page SEO & Content Strategy",
  category: "Strategy",
  description: "Search intent mapping, semantic topic clusters, high-converting copy optimization, and internal linking structure.",
  link: "/NationalSeoPage",
  icon: "FileText"
}, {
  id: "b2b",
  title: "B2B SEO & Lead Generation",
  category: "B2B SEO",
  description: "Target high-value decision makers with dedicated landing pages, technical thought leadership, and lead capture optimization.",
  link: "/B2BSeoLeadGenerationPage",
  icon: "Briefcase"
}, {
  id: "ecommerce",
  title: "Ecommerce SEO & Conversion",
  category: "Ecommerce SEO",
  description: "Product and category page optimization, faceted navigation solutions, and schema designed to capture ready-to-buy shoppers.",
  link: "/EcommerceSeoPage",
  icon: "ShoppingBag"
}, {
  id: "international",
  title: "International & Multi-Region SEO",
  category: "International SEO",
  description: "Hreflang implementation, ccTLD strategies, and localized search optimization for expanding brands globally.",
  link: "/InternationalSeoPage",
  icon: "Globe"
}, {
  id: "ai-search",
  title: "AI Search Optimisation",
  category: "AI SEO",
  description: "Prepare your brand for Google SGE, Perplexity, and ChatGPT Search by establishing entity clarity and authoritative citations.",
  link: "/AISearchOptimisationPage",
  icon: "Bot"
}, {
  id: "audits",
  title: "Comprehensive SEO Audits",
  category: "SEO Audits",
  description: "An actionable, prioritized audit that uncovers hidden blockers, technical debt, and real revenue opportunities without template filler.",
  link: "/SeoAuditsPage",
  icon: "Search"
}, {
  id: "linkbuilding",
  title: "Link Building & Authority Strategy",
  category: "Authority",
  description: "Ethical, high-impact backlink acquisition and digital PR campaigns focused on real industry relevance and sustainable growth.",
  link: "/FreelanceSeoConsultantPage",
  icon: "TrendingUp"
}];
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  const getServiceIcon = (name: string) => {
    switch (name) {
      case "MapPin":
        return <MapPin className="size-5 text-primary-foreground" />;
      case "Building2":
        return <Building2 className="size-5 text-primary-foreground" />;
      case "Star":
        return <Star className="size-5 text-primary-foreground" />;
      case "Code2":
        return <Code2 className="size-5 text-primary-foreground" />;
      case "Zap":
        return <Zap className="size-5 text-primary-foreground" />;
      case "FileText":
        return <FileText className="size-5 text-primary-foreground" />;
      case "Briefcase":
        return <Briefcase className="size-5 text-primary-foreground" />;
      case "ShoppingBag":
        return <ShoppingBag className="size-5 text-primary-foreground" />;
      case "Globe":
        return <Globe className="size-5 text-primary-foreground" />;
      case "Bot":
        return <Bot className="size-5 text-primary-foreground" />;
      case "Search":
        return <Search className="size-5 text-primary-foreground" />;
      case "TrendingUp":
        return <TrendingUp className="size-5 text-primary-foreground" />;
      default:
        return <Sparkles className="size-5 text-primary-foreground" />;
    }
  };
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesgrid" data-nav="light" className="bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border-transparent">
            {"Direct Consultant Expertise"}
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            {"Specialised SEO Services Delivered Directly By Yassine"}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {"When you partner with YE Consultancy, you work directly with me. No account managers or junior handoffs — just customized, hands-on search strategy designed for sustainable growth."}
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {servicesData.map((service, i) => <motion.div key={service.id} data-index={i} variants={cardVariants} className="h-full">
              <Card className="h-full border border-border/70 bg-card text-card-foreground shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 flex flex-col justify-between overflow-hidden group">
                <div>
                  <div className="bg-primary text-primary-foreground p-5 rounded-t-xl flex flex-col gap-3 relative">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-white/15 backdrop-blur-xs border border-white/20">
                        {getServiceIcon(service.icon)}
                      </div>
                      <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-white/15 text-primary-foreground tracking-wide">
                        {service.category}
                      </span>
                    </div>
                    <CardTitle className="text-lg font-bold leading-snug pt-1 text-primary-foreground font-serif">
                      {service.title}
                    </CardTitle>
                  </div>

                  <CardContent className="p-5 pt-5 text-sm text-muted-foreground leading-relaxed">
                    {service.description}
                  </CardContent>
                </div>

                <CardFooter className="p-5 pt-0 mt-auto">
                  <Button asChild variant="outline" className="w-full justify-between border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-[color,background-color,border-color] duration-200 group/btn">
                    <Link to={service.link}>
                      <span className="font-medium text-xs">{"Learn More"}</span>
                      <ArrowRight className="size-3.5 transition-transform group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 rounded-2xl bg-secondary/70 border border-border p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="space-y-3 text-center md:text-left max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
              <CheckCircle2 className="size-4 text-accent" />
              <span>{"Tailored Strategy Guarantee"}</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground font-serif">
              {"Not sure which SEO service fits your growth targets?"}
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              {"Book a direct consultation with me. I will evaluate your current organic footprint and present a clear, actionable roadmap tailored to your market."}
            </p>
          </div>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shrink-0">
            <Link to="/ContactPage">
              <span>{"Book a Free Consultation"}</span>
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}